import React, { useCallback, useEffect, useMemo } from 'react'
import { Box, Divider, IconButton, List, ListItem, ListItemIcon, ListItemText, ListSubheader, Menu, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { DragHandle } from '@mui/icons-material'
import { useDispatch, useSelector } from 'react-redux'
import { columnOptions } from '../../typings/options.typings'
import { strikeValues } from '../../../util/tradePage'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import Hide from '../../../assets/icons/Hide'
import Show from '../../../assets/icons/Show'
import commonActions from '../../../actions/common.actions'
import { selectOptionChainTableView, selectOptionChainVisibleStrikes, selectShowOptionChainQuantityInLots } from '../../../selectors/common'
import { selectOptionChainColumns } from '../../../selectors/home'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { matchPath } from 'react-router-dom'
import ROUTES from '../../../assets/routes'
import { OptionChainViewKey } from '../../../assets/constants'

export enum OptionChainTableView {
    NORMAL = 'NORMAL',
    CALLS = 'CALLS',
    PUTS = 'PUTS',
    OTM = 'OTM',
    ITM = 'ITM'
}

const optionChainTableView = [
    {
        key: OptionChainTableView.NORMAL,
        label: 'All',
    },
    {
        key: OptionChainTableView.CALLS,
        label: 'Calls',
    },
    {
        key: OptionChainTableView.PUTS,
        label: 'Puts',
    },
    {
        key: OptionChainTableView.OTM,
        label: 'OTM',
    },
    {
        key: OptionChainTableView.ITM,
        label: 'ITM',
    }
]

export default function OptionChainSettingsDialog({ open, setOpen, orderOptionChain = false }: {
    open: HTMLElement | undefined,
    setOpen: React.Dispatch<React.SetStateAction<HTMLElement | undefined>>
    orderOptionChain?: boolean
}) {
    const dispatch = useDispatch()
    const optionChainViewKey = useMemo(() => matchPath(ROUTES.simulator, window.location.pathname) ? OptionChainViewKey.HISTORICAL : OptionChainViewKey.LIVE, [window.location.pathname])
    const visibleStrikes: string = useSelector(selectOptionChainVisibleStrikes)
    const quantityInLots: boolean = useSelector(selectShowOptionChainQuantityInLots)
    const selectedColumns: string[] = useSelector(selectOptionChainColumns(optionChainViewKey, orderOptionChain ? 'extended' : 'normal'))
    const selectedOptionChainTableView = useSelector(selectOptionChainTableView)

    const handleClose = () => {
        setOpen(undefined)
    }


    const handleStrikeChange = useCallback((value: string) => {
        commonActions.setOptionChainVisibleStrikes(dispatch, value)
    }, [dispatch])

    const handleOptionChainTableViewChange = useCallback((view: OptionChainTableView) => {
        commonActions.setOptionChainTableView(dispatch, view)
    }, [dispatch])

    const handleDragEnd = useCallback(
        (sourceIndex: number, destinationIndex: number | undefined) => {
            if (destinationIndex === undefined) return;
            let result = [...selectedColumns].filter(c=>c!=='ask');
            const draggedItem = result[sourceIndex];
            if (draggedItem === "bid" || draggedItem === "ask") {
                const bidIndex = result.indexOf("bid");
                result.splice(bidIndex, 1)[0];
                result.splice(destinationIndex, 0, 'bid', 'ask');
            } else {
                const [removed] = result.splice(sourceIndex, 1);
                result.splice(destinationIndex, 0, removed);
                const bidIndex = result.indexOf("bid");
                if( bidIndex !== -1 ){
                    result.splice(bidIndex, 1, 'bid', 'ask');
                }
            }
            commonActions.saveOptionSelectedColumns(dispatch, {
                columns: result,
                optionChainViewKey,
                type: orderOptionChain ? "extended" : "normal",
            });
        },
        [dispatch, optionChainViewKey, selectedColumns, orderOptionChain]
    );


    return (
        <>
            <Menu
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                open={Boolean(open)}
                anchorEl={open}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                sx={{
                    height: '90vh',
                    mt: 0.5,
                    pt: 0,
                }}
                onClose={handleClose}
            >
                {
                    orderOptionChain ? null :
                        <Box component="span" sx={{ display: 'contents' }}>
                            <ListSubheader
                                sx={{
                                    lineHeight: '24px',
                                    backgroundColor: 'unset',
                                    fontSize: 12,
                                    opacity: 0.5,
                                    mt: 0,
                                }}
                            >
                                STRIKES
                            </ListSubheader>
                            <ToggleButtonGroup
                                size="small"
                                value={selectedOptionChainTableView}
                                aria-label="text alignment"
                                sx={{ mx: 2, height: "32px" }}
                            >{
                                    optionChainTableView.map((s) => {
                                        return (
                                            <ToggleButton
                                                key={s.key}
                                                aria-label="left aligned"
                                                selected={s.key == selectedOptionChainTableView}
                                                value={s.key}
                                                onClick={() => handleOptionChainTableViewChange(s.key)}
                                                sx={{ minWidth: "40px" }}
                                            >
                                                {s.label}
                                            </ToggleButton>
                                        )
                                    })
                                }
                            </ToggleButtonGroup>
                        </Box>
                }
                <ListSubheader
                    sx={{
                        lineHeight: '24px',
                        backgroundColor: 'unset',
                        fontSize: 12,
                        opacity: 0.5,
                        mt: 0,
                    }}
                >
                    NO OF STRIKES
                </ListSubheader>
                <ToggleButtonGroup
                    size="small"
                    value={visibleStrikes}
                    aria-label="text alignment"
                    sx={{ marginLeft: 2, marginRight: 2, height: "32px" }}
                >{
                        strikeValues.values.map((s) => {
                            return (<ToggleButton key={s.value} aria-label="left aligned" selected={s.value == visibleStrikes} value={s.value} onClick={() => handleStrikeChange(s.value)}
                                sx={{ minWidth: "40px" }}>
                                {s.label}
                            </ToggleButton>)
                        })
                    }
                </ToggleButtonGroup>
                <Divider sx={{ marginTop: 1 }} />
                <ListSubheader
                    sx={{
                        lineHeight: '24px',
                        backgroundColor: 'unset',
                        fontSize: 12,
                        opacity: 0.5,
                        mt: 0,
                    }}
                >
                    QUANTITY IN LOTS
                </ListSubheader>
                <ToggleButtonGroup
                    size="small"
                    value={quantityInLots ? 'Yes' : 'No'}
                    aria-label="text alignment"
                    sx={{ marginLeft: 2, height: "32px" }}
                ><ToggleButton
                    key="Yes"
                    value="Yes"
                    selected={quantityInLots}
                    sx={{ width: '50px', }}
                    onChange={() => {
                        AnalyticsManager.logEvent('Quantity in lots', {
                            source: 'option-chain-bar',
                            value: !quantityInLots
                        })
                        commonActions.setOptionChainQuantityInLots(dispatch, !quantityInLots)
                    }}
                >
                        Yes
                    </ToggleButton>
                    <ToggleButton
                        key="No"
                        value="No"
                        selected={!quantityInLots}
                        sx={{ width: '50px', }}
                        onChange={() => {
                            AnalyticsManager.logEvent('Quantity in lots', {
                                source: 'option-chain-bar',
                                value: !quantityInLots
                            })
                            commonActions.setOptionChainQuantityInLots(dispatch, !quantityInLots)
                        }}
                    >
                        No
                    </ToggleButton>
                </ToggleButtonGroup>
                {
                    orderOptionChain ? null :
                        <Box component="span" sx={{ display: 'contents' }}>
                            <Divider sx={{ marginTop: 1 }} />
                            <ListSubheader
                                sx={{
                                    lineHeight: '24px',
                                    backgroundColor: 'unset',
                                    fontSize: 12,
                                    opacity: 0.5,
                                    mt: 0,
                                }}
                            >
                                CUSTOMIZE COLUMNS
                            </ListSubheader>
                            <DragDropContext onDragEnd={({ source, destination }) => {
                                handleDragEnd(source.index, destination?.index)
                            }}>
                                <Droppable droppableId="droppable-list">
                                    {(provided: any) => (
                                        <List dense ref={provided.innerRef} {...provided.droppableProps} sx={{ paddingTop: '0px', paddingBottom: "0px" }}>
                                            {
                                                selectedColumns.filter(c=>c!=='ask').map((s, i) => {
                                                    let obj = columnOptions.find(c => c.value === s)
                                                    if (s === 'bid') {
                                                        obj = columnOptions.find(c => c.value === 'bidAsk')
                                                    }
                                                    if (!obj) return null
                                                    return (
                                                        // !selectedColumns.includes(obj.value) ? null :
                                                        <Draggable draggableId={obj.value} index={i} key={obj.value}>
                                                            {(provided: any) => (
                                                                <ListItem
                                                                    dense
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                >
                                                                    <ListItemIcon data-event={'Parameter order changed'}
                                                                        data-event-source={'option-chain-bar'}
                                                                        sx={{
                                                                            minWidth: '20px',
                                                                            cursor: 'pointer',
                                                                            marginRight: '5px',
                                                                        }}
                                                                        {...provided.dragHandleProps}>
                                                                        <DragHandle />
                                                                    </ListItemIcon>
                                                                    <ListItemText>{obj.label}</ListItemText>
                                                                    <IconButton sx={{ paddingLeft: 2, paddingY: 0, paddingRight: 0 }} onClick={() => {
                                                                        AnalyticsManager.logEvent('Parameter hidden', { source: 'option-chain-bar' })
                                                                        if (selectedColumns.length <= 1) return

                                                                        let newColumns = [...selectedColumns]
                                                                        if (obj.value === 'bidAsk') {

                                                                            newColumns = newColumns.filter(
                                                                                (s) => s !== 'bid' && s !== 'ask' && s !== 'bidAsk'
                                                                            )
                                                                        } else {
                                                                            newColumns = newColumns.filter((s) => s !== obj.value)
                                                                        }
                                                                        commonActions.saveOptionSelectedColumns(dispatch, {
                                                                            columns: newColumns,
                                                                            optionChainViewKey: optionChainViewKey,
                                                                            type: orderOptionChain ? 'extended' : 'normal'
                                                                        })
                                                                    }}>
                                                                        {
                                                                            <Show />
                                                                        }
                                                                    </IconButton>
                                                                </ListItem>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })
                                            }
                                            {provided.placeholder}
                                        </List>
                                    )}
                                </Droppable>
                            </DragDropContext>
                            {
                                columnOptions.map((c, i) => {
                                    let showAddOption = selectedColumns.includes(c.value)
                                    if (c.value === 'bidAsk') {
                                        showAddOption = selectedColumns.includes('bid')
                                    }
                                    return (
                                        showAddOption ? null :
                                            <ListItem dense key={i}
                                                sx={{ justifyContent: 'space-between' }}
                                            >
                                                <ListItemIcon sx={{ minWidth: '20px', cursor: 'pointer', visibility: 'hidden', marginRight: '5px' }}>
                                                    <DragHandle />
                                                </ListItemIcon>
                                                <ListItemText>{c.label}</ListItemText>
                                                <IconButton
                                                    sx={{ paddingLeft: 2, paddingY: 0, paddingRight: 0 }}
                                                    color="inherit"
                                                    onClick={() => {
                                                        AnalyticsManager.logEvent('Parameter shown', { source: 'option-chain-bar' })
                                                        const newArray = [...selectedColumns]
                                                        if (c.value === 'bidAsk') {

                                                            if (!newArray.includes('bid')) newArray.push('bid')
                                                            if (!newArray.includes('ask')) newArray.push('ask')
                                                            // if (!newArray.includes('bidAsk')) newArray.push('bidAsk')
                                                        } else {
                                                            newArray.push(c.value)
                                                        }
                                                        commonActions.saveOptionSelectedColumns(dispatch, {
                                                            columns: newArray,
                                                            optionChainViewKey: optionChainViewKey,
                                                            type: orderOptionChain ? 'extended' : 'normal'
                                                        })
                                                    }}
                                                >
                                                    <Hide sx={{ opacity: 0.5, padding: 0, minWidth: '20px' }} />
                                                </IconButton>

                                            </ListItem>
                                    )
                                })
                            }
                        </Box>
                }
            </Menu>
        </>
    );
}